import React, { useState } from 'react';
import {
  Typography,
  MenuItem,
  Select,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditPenIcon from '../../assests/images/editpenicon.svg';
import CalendarModal from './CalendarModal';
import { findStatusColor, monthDateFormat, statusColorCodeEnum } from '../../utils/Utils';
import TaskModal from './Modals/TaskModal';
import { getCompletedTasksCountStatus } from '../../pages/Calendar/utils';
import { sendTaskReminder, updateAssigneeStatus } from '../../pages/Calendar/service';
import {
  CORE_USER,
  GENERAL_USER,
  revertStatusOptions,
  statusOptions,
  TPO_ADMIN
} from '../../utils/Constants';
import ConfirmationModal from '../Modals/ConfirmationModal';
import { useOkta } from '../../hooks/oktaHook';
import new_tab from '../../assests/images/new_tab.svg';

const CalendarEditCell = ({
  month,
  weekTitle,
  status,
  setStatus,
  taskTitle,
  taskDescription,
  taskId,
  taskData,
  subtaskData,
  loading = false,
  fetchApi,
  enableEdit,
  userRoles,
  setShowAddTask,
  showAddTask,
  email
}) => {
  const { userDetails } = useOkta();
  // State to control the modal's open/close status
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSubTaskModal, setShowSubTaskModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [expandedKey, setExpandedKeys] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedSubTaskAction, setSelectedSubTaskAction] = useState({
    data: {},
    prinicpalTaskName: '',
    action: 'add'
  });
  const [statusObj, setStatusObj] = useState({ status: '', id: 0 });
  const [rejectMessage, setRejectMessage] = useState('');
  const cyclesData = useSelector((state) => state?.cycle);
  const tpoLeadRole = userRoles?.some((role) => [CORE_USER, TPO_ADMIN].includes(role));
  const generalUserRole = userRoles?.includes(GENERAL_USER);
  const handleOpenModal = (taskId) => {
    setSelectedTaskId(taskId);
    setIsModalOpen(true);
  };

  // Function to close the modal
  const handleCloseModal = (val) => {
    setIsModalOpen(false);
    if (val === 'submit') {
      // refetch comprehensive api data
      fetchApi();
    }
  };

  const handleCollapse = (event, isExpanded, taskId) => {
    isExpanded ? setExpandedKeys(taskId) : setExpandedKeys('');
  };

  const handleSubTaskAction = (taskData, action) => {
    setSelectedSubTaskAction({ data: taskData, prinicpalTaskName: taskTitle, action: action });
    setShowSubTaskModal(!showSubTaskModal);
  };

  const handleStatusChange = (value, id) => {
    if (tpoLeadRole && value === revertStatusOptions?.[1]) {
      setStatusObj({ status: value, id: id });
      setShowConfirmationModal(!showConfirmationModal);
    } else {
      updateAssigneeStatus(value, id, taskId, email, {}).then(() => {
        fetchApi();
      });
    }
  };

  const handleReject = () => {
    const { id } = statusObj;
    updateAssigneeStatus('In Progress', id, taskId, email, { rejectComment: rejectMessage }).then(
      () => {
        setRejectMessage('');
        fetchApi();
      }
    );
  };

  // Function to check if the reminder button should be shown
  const shouldShowReminderButton = (userDetails, item, tpoLeadRole) => {
    if (!tpoLeadRole) return false; // Ensure only TPO_ADMIN sees the button

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of the day
    const endDate = new Date(item.endDate);
    endDate.setHours(23, 59, 59, 999); // Normalize to end of the day
    const timeDiffDays = Math.floor((endDate - today) / (1000 * 60 * 60 * 24)); // Convert ms to full days

    // Find subordinates (any assignedTo user who is not the logged-in user)
    const subordinates = item.assignedTo.filter(
      (assign) => assign.assigneeEmail !== userDetails.email && assign.status !== 'Completed'
    );

    // Show button only if subordinates exist with an incomplete task
    if (subordinates.length > 0) {
      // Show Reminder if due date is in 2 days or less but not past
      if (timeDiffDays <= 2 && timeDiffDays >= 0) {
        return true;
      }

      // If task is overdue and any subordinate is not completed, show the button
      if (timeDiffDays < 0) {
        return true;
      }
    }

    return false;
  };

  const sendReminder = async (id) => {
    if (cyclesData?.selectedCycle) {
      const payload = {
        buCycleName: cyclesData?.selectedCycle,
        taskId: id
      };
      try {
        await sendTaskReminder(payload);
      } catch (error) {
        console.error('Error fetching template data:', error);
      }
    }
  };

  const toggleAddTask = (taskId) => {
    if (showAddTask?.includes(taskId)) {
      setShowAddTask(showAddTask.filter((id) => id !== taskId));
    } else {
      setShowAddTask([...showAddTask, taskId]);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          marginTop: '4px',
          backgroundColor: showAddTask?.includes(taskId) ? '#dfeaf9' : '#FBFCFE'
        }}>
        {/* Column 1: Month & Status Info */}
        <Stack
          spacing={1}
          sx={{
            p: 1,
            width: { xs: '100%', md: '35%', lg: '35%' }
          }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: findStatusColor(status, taskData, false)
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '22px'
              }}>
              {month}
            </Typography>
          </Stack>

          <Typography
            sx={{
              width: '142px',
              height: '18px',
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: '#3A3A3A',
              flex: 'none',
              order: 1,
              alignSelf: 'stretch',
              flexGrow: 0,
              textAlign: 'start'
            }}>
            {weekTitle} Week{weekTitle > 1 ? 's' : ''}
          </Typography>
        </Stack>
        {/* Column 2: Task Title & Actions */}
        <Stack
          flex={1}
          p={1}
          spacing={1}
          sx={{
            width: { xs: '100%', md: 'auto' }
          }}
          paddingBottom={!showAddTask?.includes(taskId) ? '34px' : 0}>
          <Stack flex={1} spacing={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#3A3A3A',
                  flex: '1 1 100%'
                }}>
                {taskTitle}
              </Typography>
              <Stack
                flex
                width={'100%'}
                direction="row"
                spacing={3}
                alignItems="center"
                marginRight={'8px'}>
                <IconButton
                  data-testid="edit-icon-btn"
                  disabled={!enableEdit}
                  onClick={() => handleOpenModal(taskData.taskId)}
                  style={{
                    width: 20,
                    height: 20,
                    cursor: 'pointer'
                  }}
                  alt="Edit Task Icon">
                  <img alt="Edit Task Icon" src={EditPenIcon} data-testid="edit-icon" />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '12px',
                    marginTop: '0px !important',
                    marginLeft: 'auto !important',
                    textAlign: 'end',
                    cursor: 'pointer',
                    alignSelf: 'center',
                    display: 'flex',
                    transform: showAddTask?.includes(taskId) ? 'rotate(270deg)' : 'rotate(90deg)',
                    transition: 'transform 0.3s ease-in-out'
                  }}
                  onClick={() => toggleAddTask(taskId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      toggleAddTask(taskId);
                    }
                  }}
                  title={showAddTask?.includes(taskId) ? 'Read Less' : 'Read More'}>
                  <ArrowForwardIosIcon
                    style={{
                      height: 16,
                      width: 16
                    }}
                  />
                </Typography>
              </Stack>
            </Stack>

            <Select
              data-testid="status-options"
              disabled={!enableEdit}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              variant="standard"
              disableUnderline
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '2px 4px',
                gap: '2px',
                height: '16px',
                width: 'fit-content',
                bgcolor: findStatusColor(status, taskData),
                borderRadius: '4px',
                fontSize: '12px',
                color: '#3A3A3A',
                flex: 'none',
                order: 1,
                flexGrow: 0
              }}>
              {statusOptions?.map((item) => (
                <MenuItem data-testid="status-options-value" key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          {/* Column 3: Description */}
          {taskDescription && (
            <Typography
              sx={{
                fontSize: '12px',
                color: '#3A3A3A',
                marginTop: '0px !important',
                minHeight: '25px'
              }}>
              {taskDescription}
            </Typography>
          )}
        </Stack>
        <TaskModal
          open={showSubTaskModal}
          handleClose={() => {
            setShowSubTaskModal(!showSubTaskModal);
          }}
          getDataList={() => fetchApi()}
          selectedSubTaskAction={selectedSubTaskAction}
        />
        <CalendarModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          data={taskData}
          selectedTaskId={selectedTaskId}
          screenId={'comprehensive'}
        />
      </Stack>
      {showAddTask?.includes(taskId) && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            backgroundColor: '#dfeaf9'
          }}>
          <Typography
            sx={{
              fontSize: '12px',
              marginTop: '0px !important',
              marginRight: '15px !important',
              textAlign: 'end',
              color: '#005cbc',
              cursor: 'pointer',
              fontWeight: 700
            }}
            display={'flex'}
            marginLeft={'auto'}
            alignItems="center"
            onClick={() => (tpoLeadRole ? handleSubTaskAction(taskId, 'add') : {})}
            data-testid="add-task"
            role="button"
            gap="2px"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && tpoLeadRole) {
                handleSubTaskAction(taskId, 'add');
              }
            }}>
            <AddIcon
              style={{
                height: 16,
                width: 16
              }}
            />
            <span>Add Task</span>
          </Typography>
          {subtaskData?.map((item, index) => (
            <Accordion
              key={item.id}
              expanded={expandedKey === item.id}
              onChange={(e, isExpanded) => handleCollapse(e, isExpanded, item.id)}
              style={{ boxShadow: 'none', margin: '0px 15px 50px 15px' }}>
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    style={{ marginBottom: '30px', top: '10px', position: 'relative' }}
                  />
                }
                aria-controls="panel1-content"
                id="panel1-header"
                style={{ padding: '0px 12px', alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '50%', textAlign: 'start' }}>
                      <Typography style={{ fontSize: '12px', fontWeight: 700 }}>
                        {item.subTaskName}
                        <IconButton
                          disabled={!enableEdit}
                          onClick={() => handleSubTaskAction(item, 'edit')}
                          style={{
                            width: 12,
                            height: 12,
                            cursor: 'pointer',
                            left: '5px',
                            bottom: '1px'
                          }}
                          alt="Edit Task Icon">
                          <img alt="Edit Task Icon" src={EditPenIcon} />
                        </IconButton>
                      </Typography>
                    </div>
                    <div
                      style={{
                        width: '50%',
                        display: 'flex',
                        justifyContent: 'end',
                        fontSize: '12px',
                        fontWeight: 400
                      }}>
                      {`Status: ${getCompletedTasksCountStatus(item?.assignedTo)} / ${item?.assignedTo?.length}`}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'baseline',
                      gap: '0.5rem'
                    }}>
                    <Typography style={{ fontSize: '12px', fontWeight: 400, color: '#12090E' }}>
                      {`Due : ${monthDateFormat(item.endDate)}`}
                    </Typography>
                    {shouldShowReminderButton(userDetails, item, tpoLeadRole) && (
                      <div>
                        <a
                          // href="" // will update this once API is in dev/qa
                          style={{
                            fontWeight: 400,
                            textAlign: 'left',
                            textDecoration: 'none',
                            color: '#005CBC',
                            fontSize: '12px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion toggle
                            sendReminder(item?.id);
                          }}>
                          {'Send Reminder'}
                          <img
                            src={new_tab}
                            alt="icon"
                            style={{ position: 'relative', cursor: 'pointer', fontSize: '12px' }}
                            height={12}
                            width={12}
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails style={{ padding: '0px 12px' }}>
                <div style={{ width: '100%' }}>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      textAlign: 'start',
                      fontSize: '12px',
                      fontWeight: 400,
                      minHeight: '25px'
                    }}>
                    {item.description}
                  </div>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      textAlign: 'start',
                      fontSize: '12px',
                      fontWeight: 400,
                      margin: '10px 0px'
                    }}>
                    <table style={{ width: '100%' }}>
                      <tr>
                        <td>Assigned to: </td>
                        <td>Status: </td>
                      </tr>
                      {item?.assignedTo?.map((list) => (
                        <tr key={list.assigneeName}>
                          <td>{list.assigneeName}</td>
                          <td>
                            <Select
                              value={list.status}
                              onChange={(e) => handleStatusChange(e.target.value, list.id)}
                              variant="standard"
                              data-testid={`task-status-options${list.id}`}
                              disabled={generalUserRole && list?.assigneeEmail !== email}
                              disableUnderline
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '2px 4px',
                                gap: '2px',
                                height: '16px',
                                width: 'fit-content',
                                bgcolor: statusColorCodeEnum[list.status],
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#3A3A3A',
                                flex: 'none',
                                order: 1,
                                flexGrow: 0
                              }}>
                              {(tpoLeadRole && list.status === 'Completed'
                                ? revertStatusOptions
                                : statusOptions
                              )?.map((item) => (
                                <MenuItem key={item} value={item}>
                                  {item}
                                </MenuItem>
                              ))}
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </table>
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}
      <Divider />
      <ConfirmationModal
        open={showConfirmationModal}
        title={'Completion Reject'}
        text={`You’re reverting this task to 'In Progress'. Please provide any context that may be needed for your team.`}
        cancelText="Cancel"
        SubmitText={'Send'}
        onClose={() => {
          setShowConfirmationModal(false);
        }}
        onSubmit={() => handleReject()}
        textAlign="start"
        showTextArea
        handleChange={(val) => setRejectMessage(val)}
        message={rejectMessage}
      />
    </LocalizationProvider>
  );
};

export default CalendarEditCell;
